import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useToast } from "contexts/ToastContext";
import { useCallback, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface RequestPatientConfirmModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: () => Promise<void>;
  readonly patientEmail?: string;
}

function RequestPatientConfirmModal({
  open,
  onClose,
  onSubmit,
  patientEmail,
}: RequestPatientConfirmModalProps) {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleConfirm = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onSubmit();
      showSuccess(
        "Request Sent",
        "Patient data access request has been sent successfully."
      );
      handleClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to send request. Please try again.";
      showError("Request Failed", message);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, showSuccess, showError, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      slotProps={{
        paper: {
          sx: { borderRadius: 2, minWidth: "500px" },
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            fontSize: "24px",
            paddingBottom: "8px",
          }}
        >
          Send Patient Request
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "0.5px",
            backgroundColor: colors.background.grayDivider,
            flex: "none",
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        />
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2, pb: 1 }}>
          <Typography
            sx={{
              color: colors.text.primary,
              fontSize: "16px",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Send data sharing request to{" "}
            <strong>{patientEmail || "this patient"}</strong>?
          </Typography>

          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: "14px",
            }}
          >
            The patient will receive a notification to approve the data sharing
            request.
          </Typography>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "0.5px",
            backgroundColor: colors.background.grayDivider,
            flex: "none",
            alignSelf: "stretch",
            flexGrow: 0,
            mt: 2,
          }}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "16px", justifyContent: "flex-end" }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            padding: "8px 24px",
            borderRadius: "24px",
            fontSize: "16px",
            color: colors.text.secondary,
            fontWeight: 500,
            border: `1px solid ${colors.border.light}`,
            "&:hover": { backgroundColor: colors.background.highlight },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            padding: "8px 24px",
            borderRadius: "24px",
            fontSize: "16px",
            fontWeight: 600,
            backgroundColor: colors.primary.main,
            color: colors.primary.contrast,
            "&:hover": { backgroundColor: colors.primary.dark },
            "&:disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Confirm"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RequestPatientConfirmModal;
